import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Eye, Loader2, Star, Pin } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { MarkdownEditor } from '@/components/forum/MarkdownEditor';
import { uploadBlogImageWithProgress } from '@/lib/blogUploads';
import { useToast } from '@/hooks/use-toast';

const LANGS = [
  { code: 'en', label: 'English' },
  { code: 'am', label: 'አማርኛ (Amharic)' },
  { code: 'om', label: 'Afaan Oromoo' },
] as const;
type Lang = typeof LANGS[number]['code'];

function slugify(s: string) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

const asObj = (j: any): Record<string, string> => {
  if (!j) return {};
  if (typeof j === 'string') return { en: j };
  return j as Record<string, string>;
};
const cleanI18n = (o: Record<string, string>) => {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(o)) if (v && v.trim()) out[k] = v.trim();
  return Object.keys(out).length ? out : null;
};

export default function NewsEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const isNew = !id || id === 'new';

  const { data: article, isLoading } = useQuery({
    queryKey: ['news-editor', id],
    enabled: !isNew,
    queryFn: async () => {
      const { data } = await supabase.from('news_articles').select('*').eq('id', id!).maybeSingle();
      return data;
    },
  });

  const { data: cats = [] } = useQuery({
    queryKey: ['news-categories-admin'],
    queryFn: async () => (await supabase.from('news_categories').select('*').order('sort_order')).data || [],
  });
  const { data: tagOptions = [] } = useQuery({
    queryKey: ['news-tags-admin'],
    queryFn: async () => (await supabase.from('news_tags').select('*').order('slug')).data || [],
  });

  const [title, setTitle] = useState<Record<string, string>>({});
  const [summary, setSummary] = useState<Record<string, string>>({});
  const [body, setBody] = useState<Record<string, string>>({});
  const [activeLang, setActiveLang] = useState<Lang>('en');

  const [slug, setSlug] = useState('');
  const [coverUrl, setCoverUrl] = useState('');
  const [ogImageUrl, setOgImageUrl] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [category, setCategory] = useState<string>('');
  const [tagsText, setTagsText] = useState('');
  const [isPublished, setIsPublished] = useState(false);
  const [publishedAt, setPublishedAt] = useState<string>('');
  const [scheduledFor, setScheduledFor] = useState<string>('');
  const [isFeatured, setIsFeatured] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  useEffect(() => {
    if (!article) return;
    setTitle(asObj(article.title));
    setSummary(asObj(article.summary));
    setBody(asObj(article.body));
    setSlug(article.slug || '');
    setCoverUrl(article.cover_url || '');
    setOgImageUrl(article.og_image_url || '');
    setAuthorName(article.author_name || '');
    setCategory(article.category || '');
    setTagsText((article.tags || []).join(', '));
    setIsPublished(!!article.is_published);
    setPublishedAt(article.published_at ? article.published_at.slice(0, 16) : '');
    setScheduledFor((article as any).scheduled_for ? (article as any).scheduled_for.slice(0, 16) : '');
    setIsFeatured(!!(article as any).is_featured);
    setIsPinned(!!(article as any).is_pinned);
  }, [article]);

  const tags = useMemo(() => tagsText.split(',').map((t) => t.trim()).filter(Boolean), [tagsText]);

  const onCoverUpload = async (file: File) => {
    setUploadingCover(true);
    try {
      const url = await uploadBlogImageWithProgress(file);
      setCoverUrl(url);
      toast({ title: 'Cover uploaded' });
    } catch (e: any) {
      toast({ title: 'Upload failed', description: e.message, variant: 'destructive' });
    } finally {
      setUploadingCover(false);
    }
  };

  const onSave = async () => {
    if (!title.en?.trim()) {
      toast({ title: 'English title is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    const cleanTitle = cleanI18n(title) || { en: title.en.trim() };
    const payload: any = {
      title: cleanTitle,
      summary: cleanI18n(summary),
      body: cleanI18n(body),
      cover_url: coverUrl || null,
      og_image_url: ogImageUrl || null,
      author_name: authorName || null,
      category: category || null,
      tags,
      is_published: isPublished,
      published_at: publishedAt ? new Date(publishedAt).toISOString() : new Date().toISOString(),
      scheduled_for: scheduledFor ? new Date(scheduledFor).toISOString() : null,
      is_featured: isFeatured,
      is_pinned: isPinned,
      slug: slug || slugify(title.en),
    };
    try {
      if (isNew) {
        const { data: { user } } = await supabase.auth.getUser();
        const { data, error } = await supabase
          .from('news_articles')
          .insert({ ...payload, created_by: user?.id ?? null })
          .select('id, slug')
          .single();
        if (error) throw error;
        toast({ title: 'Article created' });
        qc.invalidateQueries({ queryKey: ['news-list'] });
        navigate(`/dashboard/news/${data.id}/edit`);
      } else {
        const { error } = await supabase.from('news_articles').update(payload).eq('id', id!);
        if (error) throw error;
        toast({ title: 'Article saved' });
        qc.invalidateQueries({ queryKey: ['news-editor', id] });
        qc.invalidateQueries({ queryKey: ['news-list'] });
      }
    } catch (e: any) {
      toast({ title: 'Save failed', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (!isNew && isLoading) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="container max-w-5xl py-6 space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/dashboard/news"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Link>
          </Button>
          <h1 className="font-display text-2xl font-bold">{isNew ? 'New article' : 'Edit article'}</h1>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && article?.slug && (
            <Button variant="outline" size="sm" asChild>
              <a href={`/news/${article.slug}`} target="_blank" rel="noopener noreferrer">
                <Eye className="h-4 w-4 mr-1" /> Preview
              </a>
            </Button>
          )}
          <Button onClick={onSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
            Save
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
        <div className="space-y-5">
          <Card>
            <CardHeader><CardTitle className="text-base">Content</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <Tabs value={activeLang} onValueChange={(v) => setActiveLang(v as Lang)}>
                <TabsList>
                  {LANGS.map((l) => (
                    <TabsTrigger key={l.code} value={l.code}>
                      {l.label}
                      {title[l.code]?.trim() ? <span className="ml-1.5 h-1.5 w-1.5 rounded-full bg-primary inline-block" /> : null}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {LANGS.map((l) => (
                  <TabsContent key={l.code} value={l.code} className="space-y-4 mt-4">
                    <div>
                      <Label>Title ({l.label})</Label>
                      <Input
                        value={title[l.code] || ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setTitle((t) => ({ ...t, [l.code]: v }));
                          if (l.code === 'en' && isNew && (!slug || slug === slugify(title.en || ''))) setSlug(slugify(v));
                        }}
                      />
                    </div>
                    <div>
                      <Label>Summary ({l.label})</Label>
                      <Textarea rows={3} value={summary[l.code] || ''} onChange={(e) => setSummary((s) => ({ ...s, [l.code]: e.target.value }))} />
                    </div>
                    <div>
                      <Label>Body — Markdown ({l.label})</Label>
                      <MarkdownEditor
                        value={body[l.code] || ''}
                        onChange={(v) => setBody((b) => ({ ...b, [l.code]: v }))}
                        rows={20}
                        maxLength={50000}
                        uploader={uploadBlogImageWithProgress}
                        placeholder={`Write the article in ${l.label}…`}
                      />
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
              <div>
                <Label>Slug</Label>
                <Input value={slug} onChange={(e) => setSlug(slugify(e.target.value))} placeholder="auto-from-title" />
                <p className="text-[11px] text-muted-foreground mt-1">URL: /news/{slug || 'auto'}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-5">
          <Card>
            <CardHeader><CardTitle className="text-base">Publish</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="pub">Published now</Label>
                <Switch id="pub" checked={isPublished} onCheckedChange={setIsPublished} />
              </div>
              <div>
                <Label>Publish date</Label>
                <Input type="datetime-local" value={publishedAt} onChange={(e) => setPublishedAt(e.target.value)} />
              </div>
              <div>
                <Label>Schedule auto-publish</Label>
                <Input type="datetime-local" value={scheduledFor} onChange={(e) => setScheduledFor(e.target.value)} />
                <p className="text-[11px] text-muted-foreground mt-1">
                  When set and "Published now" is off, the article goes live automatically at this time.
                </p>
              </div>
              <div className="flex items-center justify-between">
                <Label className="inline-flex items-center gap-1.5"><Pin className="h-3.5 w-3.5" /> Pin to top</Label>
                <Switch checked={isPinned} onCheckedChange={setIsPinned} />
              </div>
              <div className="flex items-center justify-between">
                <Label className="inline-flex items-center gap-1.5"><Star className="h-3.5 w-3.5" /> Featured</Label>
                <Switch checked={isFeatured} onCheckedChange={setIsFeatured} />
              </div>
              <div>
                <Label>Author name</Label>
                <Input value={authorName} onChange={(e) => setAuthorName(e.target.value)} placeholder="Editorial Team" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Taxonomy</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Category</Label>
                <Select value={category || '__none'} onValueChange={(v) => setCategory(v === '__none' ? '' : v)}>
                  <SelectTrigger><SelectValue placeholder="Choose category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">No category</SelectItem>
                    {cats.filter((c: any) => c.is_active).map((c: any) => (
                      <SelectItem key={c.id} value={(c.name?.en) || c.slug}>{(c.name?.en) || c.slug}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tags (comma separated)</Label>
                <Input value={tagsText} onChange={(e) => setTagsText(e.target.value)} placeholder="addis-ababa, policy" />
                {tagOptions.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {tagOptions.filter((t: any) => t.is_active).map((t: any) => (
                      <Badge
                        key={t.id}
                        variant="outline"
                        className="cursor-pointer hover:border-primary"
                        onClick={() => { if (!tags.includes(t.slug)) setTagsText([...tags, t.slug].join(', ')); }}
                      >
                        +{t.slug}
                      </Badge>
                    ))}
                  </div>
                )}
                {tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {tags.map((t) => <Badge key={t}>#{t}</Badge>)}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Media</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Cover image</Label>
                {coverUrl && <img src={coverUrl} alt="" className="w-full aspect-[16/9] object-cover rounded mb-2" />}
                <div className="flex gap-2">
                  <Input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) onCoverUpload(f); }} />
                  {uploadingCover && <Loader2 className="h-4 w-4 animate-spin" />}
                </div>
                <Input value={coverUrl} onChange={(e) => setCoverUrl(e.target.value)} placeholder="or paste URL" className="mt-2" />
              </div>
              <div>
                <Label>OG image (optional)</Label>
                <Input value={ogImageUrl} onChange={(e) => setOgImageUrl(e.target.value)} placeholder="Custom social share image URL" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
